/**
 * WOLV — Clarity measurement: session tags and events.
 *
 * Tags (`clarity('set', key, value)`) describe the state of a session and are
 * filterable in Clarity; events (`clarity('event', name)`) are moments that can
 * be counted and used as funnel steps. Built for the Home Gym setup funnel
 * (tier cards → buy box → add to cart), harmless on every other page: each
 * selector simply finds nothing.
 *
 * - All calls go through the standard Clarity queue stub, so nothing waits for
 *   clarity.js (loaded by the Clarity app embed, possibly only after consent).
 * - Visibility via IntersectionObserver, once per page view, then disconnect.
 *   No scroll listeners: scroll percentages are unreliable now that the page
 *   changes height (collapsing tier cards).
 * - Clicks and changes are delegated from the document in the capture phase,
 *   so sections the theme morphs (tier cards, variant picker) keep reporting.
 *
 * Internal visitors: open any page once with ?wolv_internal=<name> (e.g. bart,
 * frank); ?wolv_internal=off clears it. Stored in localStorage per browser.
 * Every other visitor is tagged internal = false: Clarity's tag filter can
 * only include or exclude all tags of a segment at once, so external traffic
 * needs a value of its own to filter on.
 */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return;

  // Identical to the stub in Clarity's own tag: whichever runs first, the
  // other reuses it and clarity.js drains the queue once it loads.
  window.clarity =
    window.clarity ||
    function () {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };

  /** @param {...any} args */
  const c = (...args) => {
    try {
      window.clarity(...args);
    } catch (e) {
      // measurement must never break the page
    }
  };
  /** @param {string} key @param {string} value */
  const tag = (key, value) => c('set', key, value);
  /** @param {string} name */
  const ev = (name) => c('event', name);

  const fired = new Set();
  /** @param {string} name */
  const evOnce = (name) => {
    if (fired.has(name)) return;
    fired.add(name);
    ev(name);
  };

  /** Product cards and quick add show other products; they are not this page. */
  const OTHER_PRODUCT = 'product-card, quick-add-dialog, quick-add-modal, .quick-add-modal';

  /* ---------------------------------------------------------------- internal */

  let who = null;
  try {
    const param = new URLSearchParams(location.search).get('wolv_internal');
    if (param !== null) {
      const name = param.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 20);
      if (!name || name === 'off') localStorage.removeItem('wolv_internal');
      else localStorage.setItem('wolv_internal', name);
    }
    who = localStorage.getItem('wolv_internal');
  } catch (e) {
    // storage unavailable: counted as external
  }
  if (who) {
    c('identify', 'internal-' + who, undefined, undefined, 'WOLV intern · ' + who);
    tag('internal', 'true');
  } else {
    tag('internal', 'false');
  }

  /* ----------------------------------------------------------------- consent */

  /** @param {boolean} allowed */
  const setConsent = (allowed) => tag('consent_state', allowed ? 'given' : 'declined');

  document.addEventListener('visitorConsentCollected', (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail && typeof detail.analyticsAllowed === 'boolean') setConsent(detail.analyticsAllowed);
  });

  // A returning visitor gave (or refused) consent earlier: no event, read it.
  // The Customer Privacy API is loaded on demand, the same way the Clarity
  // app embed loads it.
  const readConsent = () => {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      const analytics = privacy && privacy.currentVisitorConsent && privacy.currentVisitorConsent().analytics;
      if (analytics === 'yes' || analytics === 'no') setConsent(analytics === 'yes');
    } catch (e) {
      // API not available
    }
  };
  try {
    if (window.Shopify && window.Shopify.customerPrivacy) readConsent();
    else if (window.Shopify && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) readConsent();
      });
    }
  } catch (e) {
    // no consent API on this page
  }

  /* -------------------------------------------------------------- visibility */

  /**
   * Fires `name` once when the element's top passes three quarters of the
   * viewport. A threshold of 0 with a bottom margin works for tall sections
   * too (a ratio threshold never triggers on a block taller than the screen).
   * @param {string} selector @param {string} name @param {string} [tagKey]
   */
  const seen = (selector, name, tagKey) => {
    if (!('IntersectionObserver' in window)) return;
    const element = document.querySelector(selector);
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        evOnce(name);
        if (tagKey) tag(tagKey, 'true');
        observer.disconnect();
      },
      { rootMargin: '0px 0px -25% 0px' }
    );
    observer.observe(element);
  };

  seen('[id$="__hero_home_gym"]', 'view_hero');
  seen('wolv-tier-cards', 'view_setup_cards');
  seen('.product-details .buy-buttons-block', 'view_buybox');
  seen('media-gallery', 'view_gallery');
  seen('wolv-package-components', 'view_components');
  seen('wolv-bundle-value', 'view_price_breakdown');
  seen('.wolv-faq', 'view_faq');
  seen('[id*="judge_me_reviews_review_widget"], .jm-review-widget, .jdgm-rev-widg', 'view_reviews', 'reviews_seen');

  /* -------------------------------------------------------------- tier cards */

  /** @param {Element} card */
  const tierLetter = (card) => {
    const letter = card.querySelector('.wolv-tier__letter');
    const text = letter && letter.textContent ? letter.textContent.trim().toUpperCase() : '';
    if (/^[A-Z]$/.test(text)) return text;
    const href = card.getAttribute('href') || '';
    if (href.includes('home-gym-setup-a')) return 'A';
    if (href.includes('wolv-gym')) return 'C';
    if (href.includes('home-gym-setup')) return 'B';
    return '';
  };

  // Starting state: the inline script under the cards may already have
  // collapsed them before this script runs.
  const tiersDetails = document.querySelector('details.wolv-tiers__details');
  if (tiersDetails) tag('tiers_state', tiersDetails.hasAttribute('open') ? 'open' : 'collapsed');

  // Every later change of state (user toggle, choosing the own tier, morph).
  // `toggle` does not bubble, hence the capture listener.
  document.addEventListener(
    'toggle',
    (event) => {
      const target = event.target;
      if (target instanceof HTMLDetailsElement && target.matches('details.wolv-tiers__details')) {
        tag('tiers_state', target.open ? 'open' : 'collapsed');
      }
    },
    true
  );

  /* ------------------------------------------------------------------- clicks */

  /**
   * Did this click open a closed <details>? The summary's default action runs
   * after the click listeners, so `open` still holds the old state here.
   * @param {Element} summary
   */
  const opensDetails = (summary) => {
    const details = summary.parentElement;
    return details instanceof HTMLDetailsElement && !details.open;
  };

  /** @param {string} text */
  const slug = (text) =>
    text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40);

  /** @param {Element} control */
  const presentedVideo = (control) => {
    const media = control.closest('deferred-media');
    if (!media) return null;
    const videos = media.querySelectorAll('video');
    return videos.length ? videos[videos.length - 1] : null;
  };

  document.addEventListener(
    'click',
    (event) => {
      const t = event.target instanceof Element ? event.target : null;
      if (!t) return;
      /** @type {Element | null} */
      let el;

      // Choice and configuration
      if ((el = t.closest('a.wolv-tier'))) {
        const letter = tierLetter(el);
        if (letter) {
          tag('setup_chosen', letter);
          ev('setup_card_' + letter.toLowerCase());
        }
        return;
      }
      if ((el = t.closest('summary.wolv-tiers__summary'))) {
        ev(opensDetails(el) ? 'tiers_toggle_open' : 'tiers_toggle_close');
        return;
      }
      if (t.closest('[id$="__hero_home_gym"] a, [id$="__hero_home_gym"] button')) {
        ev('hero_cta_click');
        return;
      }
      // A part opened in its bottom sheet (wolv-included-sheet): from the
      // included row, or from a card in "What's included"
      if ((el = t.closest('[data-wolv-sheet]'))) {
        const prefix = el.classList.contains('wolv-package__card-open') ? 'component_card_' : 'included_item_';
        ev(prefix + (el.dataset.wolvKind || 'open'));
        return;
      }
      // Explore page: a part under Product DNA opened in its sheet
      if ((el = t.closest('a[href^="#wolv-sheet-explore-"]'))) {
        ev('explore_part_' + (el.getAttribute('href') || '').split('-').pop());
        return;
      }
      if (t.closest('.wolv-sheet__link')) {
        ev('explore_part_view_product');
        return;
      }
      if (t.closest('.wolv-sheet')) return;
      if ((el = t.closest('.wolv-package__more > summary'))) {
        if (opensDetails(el)) ev('component_details_open');
        return;
      }
      // Overview rows under the buy box: FAQ, videos, specifications.
      // Questions inside the FAQ row report as faq_open_<slug> below.
      if ((el = t.closest('details[data-wolv-overview] > summary'))) {
        const row = /** @type {HTMLElement} */ (el.parentElement).dataset.wolvOverview;
        if (row && opensDetails(el)) ev('overview_' + row + '_open');
        return;
      }

      // Media
      if (t.closest('media-gallery .slideshow-controls__thumbnail')) {
        ev('gallery_thumb_click');
        return;
      }
      if (t.closest('media-gallery .product-media-container__zoom-button')) {
        ev('image_zoom_open');
        return;
      }
      if ((el = t.closest('media-gallery deferred-media .deferred-media__poster-button'))) {
        // The hint button on an autoplay video toggles play/pause: only a tap
        // on a paused video is a play.
        const before = presentedVideo(el);
        if (el.classList.contains('video-interaction-hint') && before && !before.paused) return;
        ev('video_play_click');
        tag('video_seen', 'true');
        const control = el;
        setTimeout(() => {
          const video = presentedVideo(control);
          if (video && video.paused && video.currentTime < 0.1) ev('video_play_failed');
        }, 3000);
        return;
      }

      // Reviews. The Judge.me widget (2026 revamp, `.jm-*`) is a client-side
      // Vue app; some of its handlers stop propagation, which the capture
      // phase runs ahead of.
      if (t.closest('[id*="judge_me_reviews_preview_badge"]')) {
        ev('reviews_header_click');
        return;
      }
      if (t.closest('[data-testid="write-review-button"], .jdgm-write-rev-link')) {
        ev('write_review_click');
        return;
      }
      if (t.closest('.jm-review-content__expand-button')) {
        ev('review_read_more');
        return;
      }
      if (t.closest('.jm-media-thumbnail-list__thumbnail')) {
        ev('review_photo_open');
        return;
      }
      if (t.closest('.jm-rating-filter__tag, .jm-search-filter__toggle')) {
        ev('review_filter_use');
        return;
      }
      // Page numbers and the prev/next arrows; a disabled arrow (prev on
      // page 1) doesn't count.
      if ((el = t.closest('.jm-pagination-controls__button'))) {
        if (!el.classList.contains('jm-button--disabled')) {
          ev('review_page_click');
          tag('reviews_paged', 'true');
        }
        return;
      }

      // Conversion
      if ((el = t.closest('.add-to-cart-button, add-to-cart-component button'))) {
        if (el.closest(OTHER_PRODUCT)) return;
        ev(el.closest('sticky-add-to-cart') ? 'atc_sticky_click' : 'atc_main_click');
        return;
      }
      if (t.closest('[name="checkout"]')) {
        ev('checkout_click');
        return;
      }
      if ((el = t.closest('summary.wolv-more-pay__summary'))) {
        if (opensDetails(el)) ev('payment_options_open');
        return;
      }
      if ((el = t.closest('summary.wolv-faq__question'))) {
        if (opensDetails(el)) ev('faq_open_' + slug(el.textContent || ''));
        return;
      }

      // Help and drop-off signals
      if (t.closest('a.custom-whatsapp, a[href*="wa.me/"]')) {
        ev('whatsapp_click');
        return;
      }
      if (t.closest('a[href*="/pages/contact"], a[href^="mailto:"], a[href^="tel:"]')) {
        ev('contact_click');
        return;
      }
      if ((el = t.closest('header-drawer summary'))) {
        if (opensDetails(el)) ev('menu_open');
        return;
      }
      if (t.closest('[on\\:click*="search-modal/showDialog"]')) {
        ev('search_open');
        return;
      }
      if (t.closest('.wolv-trust')) {
        ev('trust_bar_click');
      }
    },
    true
  );

  /* ------------------------------------------------------------------ changes */

  const tierPage = !!document.querySelector('wolv-tier-cards');

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (input instanceof HTMLSelectElement && input.matches('.jm-sort-filter__select')) {
        ev('review_sort_change');
        return;
      }
      if (!(input instanceof HTMLInputElement)) return;

      if (input.matches('.jm-search-bar__input')) {
        if (input.value.trim()) ev('review_filter_use');
        return;
      }
      if (input.matches('.wolv-readmore__state')) {
        if (input.checked) ev('lees_meer_click');
        return;
      }

      // Variant picker: data-input-id is "<option position>-<value index>".
      if (input.type !== 'radio' || !input.dataset.inputId || input.closest(OTHER_PRODUCT)) return;
      const position = input.dataset.inputId.split('-')[0];
      const number = (input.value.match(/\d+/) || [''])[0];
      if (!number) return;
      if (position === '1') {
        tag('weight', number);
        ev('weight_select_' + number);
      } else if (position === '2' && tierPage) {
        tag('tiles', number);
        ev('tiles_select_' + number);
      }
    },
    true
  );

  /* -------------------------------------------------------------- video, cart */

  // 50% of the gallery video watched; the listener removes itself.
  /** @param {Event} event */
  const onTimeUpdate = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || !video.closest('media-gallery')) return;
    if (!isFinite(video.duration) || video.duration <= 0 || video.currentTime / video.duration < 0.5) return;
    ev('video_watched_50');
    document.removeEventListener('timeupdate', onTimeUpdate, true);
  };
  document.addEventListener('timeupdate', onTimeUpdate, true);

  // Uploaded videos in the overview's video row (YouTube embeds are iframes
  // and cannot be measured from here). `play` does not bubble: capture.
  document.addEventListener(
    'play',
    (event) => {
      const video = event.target;
      if (video instanceof HTMLVideoElement && video.closest('[data-wolv-overview="videos"]')) {
        evOnce('overview_video_play');
      }
    },
    true
  );

  // Cart drawer: the dialog is kept across cart updates (data-skip-node-update),
  // so watching its open attribute covers both the cart icon and add to cart.
  const cartDialog = document.querySelector('#cart-drawer dialog');
  if (cartDialog) {
    new MutationObserver(() => {
      if (cartDialog.hasAttribute('open')) ev('cart_drawer_open');
    }).observe(cartDialog, { attributes: true, attributeFilter: ['open'] });
  }
})();
